import { createContext, createElement, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

import { EXEC_KPI_PAGE_SIZE } from "./execKpi.mjs";
import type { ExecKpiRow } from "./execKpi.d.mts";
import {
  ExecKpiFetchError,
  MAX_PAGES,
  fetchAllPages,
  isTableMissingMessage,
  probeAvailability,
  readLatestPeriod,
} from "./execKpiFetch.mjs";
import type { ProbeVerdict } from "./execKpiFetch.d.mts";
import { isColumnMissingError } from "./sanityGate";
import { getSupabase, isSupabaseConfigured } from "./supabase";
import { scopeTenant } from "./tenantScope.mjs";

/**
 * MER-674: the Executive board page's data access. Thin on purpose: the
 * paging loop, the truncation flag and the probe classification live in
 * execKpiFetch.mjs (node --test drives them with a fake builder); this file
 * only builds the scoped Supabase queries and caches the probe answer.
 *
 * Two reads, both bound to the tenant through scopeTenant (MER-245):
 *
 *   probeExecKpi     ONE head count per tenant mount, with an abort timeout,
 *                    cached in ExecKpiProvider the way flagContext.ts caches
 *                    the flag map. DashboardShell reads the answer to decide
 *                    whether the "Executive" link exists at all; the page
 *                    reads it to short-circuit to the awaiting card. Never a
 *                    query per render.
 *
 *   fetchExecKpiRows The whole tenant slice for the page, paged past
 *                    PostgREST's 1,000-row cap with an explicit total order
 *                    (unordered pages overlap; the spec's appendix records
 *                    the phantom duplicates that produced). ~8,000 rows per
 *                    tenant today (391 line items x 19 months), so eight
 *                    pages. If MAX_PAGES come back full the result carries
 *                    `truncated` and the page says so.
 *
 *   fetchExecKpiLatestPeriod  MER-692. ONE row, only after the probe says
 *                    "available": the workbook's latest period, which the
 *                    dashboard's "Workbook month" chip selects. Also cached
 *                    in ExecKpiProvider, so still one probe per tenant.
 *
 * The exec_kpi table lands in MER-667. Until that migration is applied the
 * table does not exist: PostgREST answers PGRST205, both classifiers agree
 * it is "missing", and neither read throws a raw error at a client.
 */

export { ExecKpiFetchError, MAX_PAGES };

export const EXEC_KPI_TABLE = "exec_kpi";

export const EXEC_KPI_COLUMNS =
  "sheet,division,equipment_type,is_rollup,block,line_item,line_order,period,value,cell_text,unit,decimals,additive,provenance,note,workbook_latest_period,created_date";

const PROBE_TIMEOUT_MS = 8000;

export type ExecKpiAvailability = "pending" | ProbeVerdict;

export interface ExecKpiFetchResult {
  rows: ExecKpiRow[];
  truncated: boolean;
  pages: number;
}

/** Both "table missing" classifiers, so the probe and the read agree. */
function isMissing(message: string): boolean {
  return isTableMissingMessage(message) || isColumnMissingError(message);
}

/**
 * Every exec_kpi row for the tenant, in (sheet, line_order, line_item,
 * period) order. Throws ExecKpiFetchError; `tableMissing` is true when the
 * table is not in the schema yet.
 */
export async function fetchExecKpiRows(tenantId: string): Promise<ExecKpiFetchResult> {
  const supabase = getSupabase();
  const result = await fetchAllPages<ExecKpiRow>(
    (from, to) =>
      scopeTenant(supabase.from(EXEC_KPI_TABLE).select(EXEC_KPI_COLUMNS), tenantId)
        .order("sheet", { ascending: true })
        .order("line_order", { ascending: true, nullsFirst: false })
        .order("line_item", { ascending: true })
        .order("period", { ascending: true })
        .range(from, to),
    { pageSize: EXEC_KPI_PAGE_SIZE, maxPages: MAX_PAGES, isMissing },
  );
  return { rows: result.rows, truncated: result.truncated, pages: result.pages };
}

/**
 * Does this tenant have any exec_kpi rows? One head count, aborted after
 * PROBE_TIMEOUT_MS so it can never hang the shell.
 *
 *   missing    the table is not in the schema (PGRST205 / 42P01), or
 *              Supabase is not configured at all
 *   empty      the table exists and the tenant has zero rows
 *   available  at least one row
 *   error      any other failure (RLS, timeout, network); loud in the
 *              console, silent in the UI (the MER-464 rule)
 */
export async function probeExecKpi(tenantId: string): Promise<ExecKpiAvailability> {
  return probeAvailability(
    () =>
      scopeTenant(
        getSupabase().from(EXEC_KPI_TABLE).select("tenant_id", { count: "exact", head: true }),
        tenantId,
      ).abortSignal(AbortSignal.timeout(PROBE_TIMEOUT_MS)),
    {
      configured: isSupabaseConfigured(),
      isMissing,
      warn: (message) => console.warn(message),
    },
  );
}

/**
 * MER-692: the workbook's latest period ('YYYY-MM'), or null when it is not
 * known. One small tenant-scoped read: the newest row's
 * workbook_latest_period. Resolves to null on any failure and warns in the
 * console; the classification lives in execKpiFetch.mjs (readLatestPeriod),
 * so no database text can reach a client.
 */
export async function fetchExecKpiLatestPeriod(tenantId: string): Promise<string | null> {
  return readLatestPeriod(
    () =>
      scopeTenant(getSupabase().from(EXEC_KPI_TABLE).select("workbook_latest_period"), tenantId)
        .order("period", { ascending: false })
        .limit(1)
        .abortSignal(AbortSignal.timeout(PROBE_TIMEOUT_MS)),
    { warn: (message) => console.warn(message) },
  );
}

const ExecKpiContext = createContext<ExecKpiAvailability>("pending");
const ExecKpiLatestPeriodContext = createContext<string | null>(null);

/**
 * Probes once per tenant and hands every consumer the cached answer. Mounted
 * next to FlagMapProvider in CustomerDashboard so the shell (nav link) and
 * the page share one request.
 */
export function ExecKpiProvider(props: { tenantId: string | null; children: ReactNode }) {
  const { tenantId, children } = props;
  const [availability, setAvailability] = useState<ExecKpiAvailability>("pending");
  const [latestPeriod, setLatestPeriod] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setAvailability("pending");
      setLatestPeriod(null);
      return;
    }
    let cancelled = false;
    setAvailability("pending");
    setLatestPeriod(null);
    probeExecKpi(tenantId).then((next) => {
      if (cancelled) return;
      setAvailability(next);
      // MER-692: only a tenant WITH workbook rows gets the second read.
      if (next !== "available") return;
      fetchExecKpiLatestPeriod(tenantId).then((period) => {
        if (!cancelled) setLatestPeriod(period);
      });
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  return createElement(
    ExecKpiContext.Provider,
    { value: availability },
    createElement(ExecKpiLatestPeriodContext.Provider, { value: latestPeriod }, children),
  );
}

export function useExecKpiAvailability(): ExecKpiAvailability {
  return useContext(ExecKpiContext);
}

/** MER-692: the workbook's latest period, or null (unknown, loading, or no workbook). */
export function useExecKpiLatestPeriod(): string | null {
  return useContext(ExecKpiLatestPeriodContext);
}
